import { NAV_ITEMS } from '@/constants';
import { DashboardRouteParams, NavigationItems } from '@/types';
import { useCallback, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { usePlaceSpecificUrl } from './usePlaceSpecificUrl';

const NAVIGATION_ITEM_VALUES = Object.values(NAV_ITEMS);

function isNavigationItem(value: string | undefined): value is NavigationItems {
  if (!value) return false;
  return NAVIGATION_ITEM_VALUES.includes(value as NavigationItems);
}

/**
 * Custom hook to get the active tab from the URL parameters.
 * It utilizes `useParams` from `react-router-dom` to extract the 'tab' parameter.
 *
 * @returns The current active tab string, or undefined if not present in the URL.
 */
export const useRootTab = () => {
  const location = useLocation();
  const params = useParams<DashboardRouteParams>();

  const activeTab = useMemo(() => {
    if (isNavigationItem(params.tab)) {
      return params.tab;
    }
    // Try to derive from path if params.tab is not a valid NavigationItem
    // Path structure is usually /:region/:tab/...
    // where tab could be 'complaints', 'home', etc.
    const pathSegments = location.pathname.split('/');
    if (
      params.region &&
      pathSegments.length > 2 &&
      pathSegments[1] === params.region
    ) {
      const potentialTabSegment = pathSegments[2];
      if (isNavigationItem(potentialTabSegment)) {
        return potentialTabSegment;
      }
    }
  }, [location.pathname, params.tab, params.region]);

  const history = useHistory();
  const { getPlaceSpecificUrl } = usePlaceSpecificUrl();

  const goToTab = useCallback(
    (tab: NavigationItems) => {
      history.push(getPlaceSpecificUrl(tab));
    },
    [getPlaceSpecificUrl, history]
  );

  return { activeTab, goToTab };
};
